import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { fromBlob } from 'geotiff';
import proj4 from 'proj4';
import {
  ArrowLeft, Save, ChevronDown, ChevronUp, Upload,
  CheckCircle2, AlertCircle, Loader2, ImagePlus, Code2, X,
} from 'lucide-react';
import { supabase } from '../../supabase';
import GeoMapPreview, { OverlayLayer } from '../../components/GeoMapPreview';
import type { EmpFinca, EmpLote } from '../../types/empacadora';

const MAX_RASTER_UPLOAD_BYTES = 8 * 1024 * 1024;
const MAX_SOURCE_TIF_BYTES = 750 * 1024 * 1024;

function formatMb(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export interface LoteWorkspaceProps {
  finca: EmpFinca;
  lote?: EmpLote;
  empresaId: number;
  onSaved: (lote: EmpLote) => void;
  onClose: () => void;
}

// ─── GeoTIFF helpers ──────────────────────────────────────────────────────────

function inferEpsg(geoKeys: any): number {
  const proj = Number(geoKeys?.ProjectedCSTypeGeoKey || 0);
  const geog = Number(geoKeys?.GeographicTypeGeoKey || 0);
  if (proj > 0) return proj;
  if (geog > 0) return geog;
  const m = String(geoKeys?.GTCitationGeoKey || '').match(/UTM\s*zone\s*(\d{1,2})\s*([NS])/i);
  if (m) { const z = Number(m[1]); return m[2].toUpperCase() === 'S' ? 32700 + z : 32600 + z; }
  return 0;
}

function toWgs84(epsg: number, x: number, y: number): [number, number] {
  if (epsg === 4326) return [x, y];
  let from = '';
  if (epsg >= 32601 && epsg <= 32660) from = `+proj=utm +zone=${epsg - 32600} +datum=WGS84 +units=m +no_defs`;
  else if (epsg >= 32701 && epsg <= 32760) from = `+proj=utm +zone=${epsg - 32700} +south +datum=WGS84 +units=m +no_defs`;
  else throw new Error(`CRS EPSG:${epsg} no soportado. Exportá el GeoTIFF en WGS84 o UTM.`);
  const [lng, lat] = proj4(from, 'WGS84', [x, y]);
  return [lng, lat];
}

async function geotiffToBlob(file: File): Promise<{ blob: Blob; bounds: [[number, number], [number, number]] }> {
  const tiff = await fromBlob(file);
  const img = await tiff.getImage();
  const geoKeys = img.getGeoKeys();
  const epsg = inferEpsg(geoKeys);
  if (!epsg) throw new Error('El GeoTIFF no tiene CRS. Exportalo en WGS84 o UTM.');

  const bbox = img.getBoundingBox();
  const [minLng, minLat] = toWgs84(epsg, bbox[0], bbox[1]);
  const [maxLng, maxLat] = toWgs84(epsg, bbox[2], bbox[3]);

  const MAX_DIM = 1200;
  const srcW = img.getWidth();
  const srcH = img.getHeight();
  const scale = Math.min(1, MAX_DIM / Math.max(srcW, srcH));
  const outW = Math.round(srcW * scale);
  const outH = Math.round(srcH * scale);

  const rasters = await img.readRasters({ width: outW, height: outH, interleave: true });
  const data = rasters as unknown as Uint8Array;
  const spp = img.getSamplesPerPixel();

  const canvas = document.createElement('canvas');
  canvas.width = outW; canvas.height = outH;
  const ctx = canvas.getContext('2d')!;
  const imgData = ctx.createImageData(outW, outH);

  for (let i = 0; i < outW * outH; i++) {
    if (spp >= 3) {
      imgData.data[i * 4 + 0] = Math.min(255, Math.round(Number(data[i * spp + 0])));
      imgData.data[i * 4 + 1] = Math.min(255, Math.round(Number(data[i * spp + 1])));
      imgData.data[i * 4 + 2] = Math.min(255, Math.round(Number(data[i * spp + 2])));
      imgData.data[i * 4 + 3] = spp >= 4 ? Math.min(255, Math.round(Number(data[i * spp + 3]))) : 255;
    } else {
      const v = Math.min(255, Math.round(Number(data[i])));
      imgData.data[i * 4 + 0] = v; imgData.data[i * 4 + 1] = v;
      imgData.data[i * 4 + 2] = v; imgData.data[i * 4 + 3] = 255;
    }
  }
  ctx.putImageData(imgData, 0, 0);

  const blob = await new Promise<Blob>((res, rej) =>
    canvas.toBlob(b => b ? res(b) : rej(new Error('toBlob falló')), 'image/png')
  );
  if (blob.size > MAX_RASTER_UPLOAD_BYTES) {
    throw new Error(`El raster procesado pesa ${formatMb(blob.size)}. Reducilo antes de subirlo; limite ${formatMb(MAX_RASTER_UPLOAD_BYTES)}.`);
  }
  return { blob, bounds: [[minLat, minLng], [maxLat, maxLng]] };
}

async function subirRaster(blob: Blob, loteId: number, empresaId: number): Promise<string> {
  if (blob.size > MAX_RASTER_UPLOAD_BYTES) {
    throw new Error(`El raster supera ${formatMb(MAX_RASTER_UPLOAD_BYTES)} y no se subio a Storage.`);
  }
  const path = `${empresaId}/${loteId}.png`;
  const { error } = await supabase.storage
    .from('drone-fotos')
    .upload(path, blob, { contentType: 'image/png', upsert: true });
  if (error) throw error;
  const { data } = supabase.storage.from('drone-fotos').getPublicUrl(path);
  // Cache-bust para que el browser no muestre la imagen anterior
  return `${data.publicUrl}?t=${Date.now()}`;
}

// ─── Workspace ────────────────────────────────────────────────────────────────

function Workspace({ finca, lote, empresaId, onSaved, onClose }: LoteWorkspaceProps) {
  const [persianaOpen, setPersianaOpen] = useState(true);

  const [form, setForm] = useState({
    numero:           lote?.numero           ?? '',
    nombre:           lote?.nombre           ?? '',
    area_ha:          lote?.area_ha          ?? ('' as any),
    area_ha_sembrada: lote?.area_ha_sembrada ?? ('' as any),
    variedad:         lote?.variedad         ?? 'MD2',
  });
  const setF = (k: keyof typeof form, v: any) => setForm(f => ({ ...f, [k]: v }));

  // raster: {url, bounds} — url puede ser objectURL (recién cargado) o URL pública de SB (editando)
  const [raster, setRaster] = useState<{ url: string; bounds: [[number, number], [number, number]]; blob?: Blob } | null>(() => {
    if (lote?.raster_png_url && lote?.raster_bounds) {
      return { url: lote.raster_png_url, bounds: lote.raster_bounds };
    }
    return null;
  });

  const [tifFile, setTifFile]     = useState<File | null>(null);
  const [loadingImg, setLoadingImg] = useState(false);
  const [imgErr, setImgErr]       = useState('');
  const tifInputRef               = useRef<HTMLInputElement>(null);

  // Calcula área en hectáreas a partir de un GeoJSON polygon (coordenadas WGS84)
  function calcAreaHa(gj: any): number {
    try {
      const coords: [number, number][] = gj?.geometry?.coordinates?.[0] ?? gj?.coordinates?.[0] ?? [];
      if (coords.length < 3) return 0;
      const R = 6371000; // radio terrestre en metros
      const toRad = (d: number) => d * Math.PI / 180;
      let area = 0;
      for (let i = 0; i < coords.length - 1; i++) {
        const [lng1, lat1] = coords[i];
        const [lng2, lat2] = coords[i + 1];
        area += toRad(lng2 - lng1) * (2 + Math.sin(toRad(lat1)) + Math.sin(toRad(lat2)));
      }
      area = Math.abs(area * R * R / 2) / 10000; // m² → ha
      return parseFloat(area.toFixed(4));
    } catch { return 0; }
  }

  const [geojson, setGeojson] = useState<any>(lote?.geojson ?? null);
  const [drawResetKey]        = useState(0);
  const [bloqueOverlays, setBloqueOverlays] = useState<OverlayLayer[]>([]);
  const [geojsonEditorOpen, setGeojsonEditorOpen] = useState(false);
  const [geojsonText, setGeojsonText]             = useState('');
  const [geojsonEditorErr, setGeojsonEditorErr]   = useState('');

  // Cargar bloques existentes para mostrarlos en el mapa (usa geometry WGS84 del motor)
  useEffect(() => {
    if (!lote?.id) return;
    supabase.from('emp_bloques')
      .select('num, conteo_resultado_payload')
      .eq('lote_id', lote.id)
      .then(({ data }) => {
        if (!data?.length) return;
        const layers: OverlayLayer[] = (data ?? [])
          .map(b => {
            const geom = b.conteo_resultado_payload?.geometry_export
                      ?? b.conteo_resultado_payload?.geometry;
            if (!geom) return null;
            return {
              color: '#f97316',
              geojson: { type: 'Feature', geometry: geom, properties: { num: b.num } },
            };
          })
          .filter(Boolean) as OverlayLayer[];
        setBloqueOverlays(layers);
      });
  }, [lote?.id]);

  function onPoligonoCommit(gj: any) {
    setGeojson(gj);
    const ha = calcAreaHa(gj);
    if (ha > 0) setF('area_ha', ha);
  }

  const [saving, setSaving]   = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [saveOk, setSaveOk]   = useState(false);

  // Limpiar objectURL al desmontar para no perder memoria
  useEffect(() => {
    return () => {
      if (raster?.blob && raster.url.startsWith('blob:')) URL.revokeObjectURL(raster.url);
    };
  }, [raster]);

  async function cargarTif(file: File) {
    setLoadingImg(true); setImgErr('');
    try {
      const isTiff = /\.(tif|tiff)$/i.test(file.name);
      if (isTiff && file.size > MAX_SOURCE_TIF_BYTES) {
        throw new Error(`El GeoTIFF pesa ${formatMb(file.size)}. Limite de procesamiento: ${formatMb(MAX_SOURCE_TIF_BYTES)}.`);
      }
      if (!isTiff && file.size > MAX_RASTER_UPLOAD_BYTES) {
        throw new Error(`El archivo pesa ${formatMb(file.size)}. Limite para PNG/JPG directo: ${formatMb(MAX_RASTER_UPLOAD_BYTES)}.`);
      }
      if (isTiff) {
        const { blob, bounds } = await geotiffToBlob(file);
        const url = URL.createObjectURL(blob);
        setRaster({ url, bounds, blob });
      } else {
        const blob = file;
        const url = URL.createObjectURL(blob);
        setRaster({ url, bounds: [[0, 0], [1, 1]], blob });
      }
      setTifFile(file);
    } catch (e: any) {
      setImgErr(e?.message ?? 'Error al leer el archivo.');
    }
    setLoadingImg(false);
  }

  async function guardar() {
    if (!form.numero.trim()) { setSaveErr('El número de lote es obligatorio.'); return; }
    if (!form.nombre.trim()) { setSaveErr('El nombre es obligatorio.'); return; }
    // El perímetro (geojson) es opcional: se puede guardar el lote sin mapa y
    // dibujarlo después. Si no hay, se envía null.
    setSaving(true); setSaveErr(''); setSaveOk(false);
    try {
      const payload: any = {
        empresa_id:       empresaId,
        finca_id:         finca.id,
        numero:           form.numero.trim(),
        nombre:           form.nombre.trim(),
        area_ha:          form.area_ha          !== '' ? parseFloat(String(form.area_ha))          : null,
        area_ha_sembrada: form.area_ha_sembrada !== '' ? parseFloat(String(form.area_ha_sembrada)) : null,
        variedad:         form.variedad,
        geojson,
        activo:           true,
      };

      // Si hay bounds ya los incluimos (la URL se actualiza después del upload)
      if (raster) {
        payload.raster_bounds      = raster.bounds;
        payload.raster_uploaded_at = new Date().toISOString();
      }

      // 1. Insertar / actualizar el lote para obtener el ID
      let saved: EmpLote;
      if (lote) {
        const { data, error } = await supabase.from('emp_lotes').update(payload).eq('id', lote.id).select().single();
        if (error) throw error;
        saved = data as EmpLote;
      } else {
        const { data, error } = await supabase.from('emp_lotes').insert(payload).select().single();
        if (error) throw error;
        saved = data as EmpLote;
      }

      // 2. Si hay blob nuevo, subir a Storage y guardar URL
      if (raster?.blob) {
        const publicUrl = await subirRaster(raster.blob, saved.id, empresaId);
        const { data, error } = await supabase
          .from('emp_lotes')
          .update({ raster_png_url: publicUrl })
          .eq('id', saved.id)
          .select()
          .single();
        if (error) throw error;
        saved = data as EmpLote;
      }

      setSaveOk(true);
      onSaved(saved);
    } catch (e: any) {
      setSaveErr(e?.message ?? 'Error al guardar.');
    } finally {
      setSaving(false);
    }
  }

  const inputCls  = 'w-full px-2.5 py-1.5 rounded text-sm';
  const inputStyle = { background: 'var(--surface-overlay)', border: '1px solid var(--line)', color: 'var(--ink)' };
  const labelStyle = { color: 'var(--ink-faint)' };

  return (
    <>
    <div className="fixed inset-0 z-[9999] flex flex-col" style={{ background: 'var(--surface-base)' }}>

      {/* ── Header ── */}
      <div className="flex items-center gap-3 px-4 py-2.5 shrink-0"
        style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-raised)', minHeight: 48 }}>

        <button onClick={onClose}
          className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm shrink-0"
          style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
          <ArrowLeft size={14} /> Volver
        </button>

        <div className="flex-1 min-w-0 flex items-center gap-1.5 text-sm">
          <span className="font-medium truncate" style={{ color: 'var(--ink-faint)' }}>{finca.nombre}</span>
          <span style={{ color: 'var(--ink-faint)' }}>›</span>
          <span className="font-bold truncate" style={{ color: 'var(--ink)' }}>
            {lote ? `Lote ${lote.numero} — ${lote.nombre}` : 'Nuevo Lote'}
          </span>
        </div>

        <button type="button" onClick={() => tifInputRef.current?.click()}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border shrink-0"
          style={{ borderColor: 'var(--tone-info-border)', color: 'var(--tone-info-text)', background: 'var(--tone-info-bg)' }}>
          <Upload size={12} />
          {tifFile ? tifFile.name.slice(0, 24) + '…' : raster ? 'Reemplazar TIF' : 'Cargar TIF del dron'}
        </button>
        <input ref={tifInputRef} type="file" accept=".tif,.tiff,.png,.jpg" className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) cargarTif(f); }} />

        <button onClick={guardar} disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-sm font-semibold shrink-0"
          style={{ background: 'var(--emp-accent)', color: '#fff', opacity: saving ? 0.6 : 1 }}>
          {saving
            ? <><Loader2 size={13} className="animate-spin" /> Guardando…</>
            : <><Save size={13} /> Guardar lote</>}
        </button>
      </div>

      {/* ── Persiana datos ── */}
      <div className="shrink-0 overflow-hidden" style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-raised)', maxHeight: persianaOpen ? 200 : 40, transition: 'max-height 0.2s ease' }}>
        <button
          className="w-full flex items-center gap-2 px-4 py-2 text-xs"
          style={{ color: 'var(--ink-faint)' }}
          onClick={() => setPersianaOpen(o => !o)}>
          {persianaOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          <span className="font-semibold uppercase tracking-wide">Datos del lote</span>
          {saveOk && (
            <span className="ml-2 flex items-center gap-1" style={{ color: 'var(--tone-success-text)' }}>
              <CheckCircle2 size={11} /> Guardado
            </span>
          )}
          {saveErr && (
            <span className="ml-2 flex items-center gap-1 text-xs" style={{ color: 'var(--tone-warn-text)' }}>
              <AlertCircle size={11} />{saveErr}
            </span>
          )}
        </button>

        {persianaOpen && (
          <div className="px-4 pb-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wide block mb-1" style={labelStyle}>N° Lote *</label>
              <input value={form.numero} onChange={e => setF('numero', e.target.value)} placeholder="12"
                className={inputCls} style={inputStyle} />
            </div>
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-wide block mb-1" style={labelStyle}>Nombre *</label>
              <input value={form.nombre} onChange={e => setF('nombre', e.target.value)} placeholder="Lote Norte"
                className={inputCls} style={inputStyle} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide block mb-1" style={labelStyle}>Área total (ha)</label>
              <div className="flex gap-1">
                <input type="number" step="0.01" min="0" value={form.area_ha}
                  onChange={e => setF('area_ha', e.target.value)} placeholder="0.00"
                  className={inputCls} style={{ ...inputStyle, flex: 1 }} />
                {geojson && (
                  <button type="button" title="Recalcular área desde polígono"
                    onClick={() => { const ha = calcAreaHa(geojson); if (ha > 0) setF('area_ha', ha); }}
                    className="px-2 rounded text-xs shrink-0"
                    style={{ background: 'var(--surface-overlay)', border: '1px solid var(--line)', color: 'var(--ink-faint)' }}>
                    ↺
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide block mb-1" style={labelStyle}>Área sembrada (ha)</label>
              <div className="flex gap-1">
                <input type="number" step="0.01" min="0" value={form.area_ha_sembrada}
                  onChange={e => setF('area_ha_sembrada', e.target.value)} placeholder="0.00"
                  className={inputCls} style={{ ...inputStyle, flex: 1 }} />
                {lote?.id && (
                  <button type="button" title="Recalcular desde suma de bloques"
                    onClick={async () => {
                      const { data } = await supabase.from('emp_bloques')
                        .select('area_ha')
                        .eq('lote_id', lote.id)
                        .eq('activo', true);
                      const total = (data ?? []).reduce((s, b) => s + (b.area_ha ?? 0), 0);
                      if (total > 0) setF('area_ha_sembrada', parseFloat(total.toFixed(4)));
                    }}
                    className="px-2 rounded text-xs shrink-0"
                    style={{ background: 'var(--surface-overlay)', border: '1px solid var(--line)', color: 'var(--ink-faint)' }}>
                    ↺
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide block mb-1" style={labelStyle}>Variedad</label>
              <select value={form.variedad} onChange={e => setF('variedad', e.target.value)}
                className={inputCls} style={inputStyle}>
                <option value="MD2">MD2 Golden</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* ── Área principal ── */}
      <div className="flex-1 min-h-0 relative">

        {loadingImg && (
          <div className="absolute inset-0 flex items-center justify-center gap-3 z-20"
            style={{ background: 'var(--surface-base)' }}>
            <Loader2 size={32} className="animate-spin" style={{ color: 'var(--emp-accent)' }} />
            <span className="text-sm" style={{ color: 'var(--ink-faint)' }}>Procesando GeoTIFF...</span>
          </div>
        )}

        {!raster && !loadingImg && !geojson && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-4 cursor-pointer"
            style={{ background: 'var(--surface-base)' }}
            onClick={() => tifInputRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) cargarTif(f); }}>
            <ImagePlus size={52} style={{ color: 'var(--ink-faint)', opacity: 0.3 }} />
            <div className="text-center">
              <p className="text-base font-semibold mb-1" style={{ color: 'var(--ink)' }}>
                Arrastrá o hacé clic para cargar el GeoTIFF del dron
              </p>
              <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>
                DJI Terra · Pix4D · OpenDroneMap — debe tener CRS (WGS84 o UTM)
              </p>
              {imgErr && (
                <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg"
                  style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)' }}>
                  <AlertCircle size={14} style={{ color: 'var(--tone-warn-text)' }} />
                  <span className="text-sm" style={{ color: 'var(--tone-warn-ink)' }}>{imgErr}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {!raster && !loadingImg && geojson && (
          <>
            <GeoMapPreview
              geojson={geojson}
              height="100%"
              maxWidthClassName="max-w-full"
              polygonColor="#38bdf8"
              editable
              drawResetKey={drawResetKey}
              onDrawCommit={onPoligonoCommit}
              overlays={bloqueOverlays.length ? bloqueOverlays : undefined}
            />
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 pointer-events-none z-10">
              <span className="text-xs px-3 py-1.5 rounded-full whitespace-nowrap"
                style={{ background: 'rgba(0,0,0,0.7)', color: '#e2e8f0' }}>
                ✓ Perímetro guardado · Cargá el TIF para ver la ortofoto
              </span>
            </div>
            <div className="absolute top-3 right-3 z-10">
              <button
                onClick={() => { setGeojsonText(JSON.stringify(geojson, null, 2)); setGeojsonEditorErr(''); setGeojsonEditorOpen(true); }}
                className="text-xs px-3 py-1.5 rounded-lg border flex items-center gap-1.5"
                style={{ background: 'rgba(0,0,0,0.65)', borderColor: 'rgba(255,255,255,0.2)', color: '#e2e8f0' }}>
                <Code2 size={12} /> Editar GeoJSON
              </button>
            </div>
          </>
        )}

        {raster && !loadingImg && (
          <>
            <GeoMapPreview
              geojson={geojson}
              height="100%"
              maxWidthClassName="max-w-full"
              polygonColor="#38bdf8"
              editable
              drawResetKey={drawResetKey}
              onDrawCommit={onPoligonoCommit}
              rasterOverlay={{ url: raster.url, bounds: raster.bounds, opacity: 0.9 }}
            />
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 pointer-events-none z-10">
              <span className="text-xs px-3 py-1.5 rounded-full whitespace-nowrap"
                style={{ background: 'rgba(0,0,0,0.7)', color: '#e2e8f0' }}>
                {geojson
                  ? '✓ Perímetro listo — guardá el lote para continuar con los bloques'
                  : 'Hacé clic para marcar los vértices del lote (mín. 4 puntos) · doble clic para cerrar'}
              </span>
            </div>
            <div className="absolute top-3 right-3 z-10 flex gap-2">
              <button
                onClick={() => { setGeojsonText(JSON.stringify(geojson, null, 2)); setGeojsonEditorErr(''); setGeojsonEditorOpen(true); }}
                className="text-xs px-3 py-1.5 rounded-lg border flex items-center gap-1.5"
                style={{ background: 'rgba(0,0,0,0.65)', borderColor: 'rgba(255,255,255,0.2)', color: '#e2e8f0' }}>
                <Code2 size={12} /> Editar GeoJSON
              </button>
              <button
                onClick={() => { setRaster(null); setTifFile(null); setImgErr(''); }}
                className="text-xs px-3 py-1.5 rounded-lg border"
                style={{ background: 'rgba(0,0,0,0.6)', borderColor: 'rgba(255,255,255,0.2)', color: '#e2e8f0' }}>
                Cambiar imagen
              </button>
            </div>
          </>
        )}
      </div>
    </div>

    {/* Modal editor GeoJSON — portal */}
    {geojsonEditorOpen && ReactDOM.createPortal(
      <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.8)' }}>
        <div className="flex flex-col rounded-xl shadow-2xl overflow-hidden" style={{ width: 640, maxHeight: '85vh', background: 'var(--surface-overlay)', border: '1px solid var(--line)' }}>
          <div className="flex items-center justify-between px-4 py-3 shrink-0" style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
            <span className="font-semibold text-sm flex items-center gap-2" style={{ color: 'var(--ink)' }}>
              <Code2 size={14} style={{ color: 'var(--emp-accent-txt)' }} /> Editar GeoJSON del perímetro
            </span>
            <button onClick={() => setGeojsonEditorOpen(false)} style={{ color: 'var(--ink-faint)' }}><X size={15} /></button>
          </div>
          <div className="p-4 flex flex-col gap-3 flex-1 overflow-hidden">
            <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>
              Pegá el GeoJSON corregido desde <b style={{ color: 'var(--ink)' }}>geojson.io</b> — puede ser Feature, FeatureCollection o Geometry.
            </p>
            <textarea
              value={geojsonText}
              onChange={e => { setGeojsonText(e.target.value); setGeojsonEditorErr(''); }}
              spellCheck={false}
              className="flex-1 w-full rounded p-3 text-xs font-mono resize-none"
              style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink)', minHeight: 320 }}
            />
            {geojsonEditorErr && (
              <div className="flex items-center gap-2 px-3 py-2 rounded text-xs"
                style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)', color: 'var(--tone-warn-text)' }}>
                <AlertCircle size={12} /> {geojsonEditorErr}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 px-4 py-3 shrink-0" style={{ borderTop: '1px solid var(--line)' }}>
            <button onClick={() => setGeojsonEditorOpen(false)}
              className="px-4 py-1.5 rounded text-xs border" style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
              Cancelar
            </button>
            <button
              onClick={() => {
                try {
                  const parsed = JSON.parse(geojsonText);
                  // Normalizar a geometry
                  let geom = parsed;
                  if (parsed.type === 'FeatureCollection') geom = parsed.features?.[0]?.geometry ?? parsed;
                  else if (parsed.type === 'Feature') geom = parsed.geometry;
                  if (!geom || !['Polygon','MultiPolygon'].includes(geom.type)) {
                    setGeojsonEditorErr('Debe ser un Polygon o MultiPolygon.');
                    return;
                  }
                  setGeojson(geom);
                  setGeojsonEditorOpen(false);
                } catch {
                  setGeojsonEditorErr('JSON inválido. Revisá la sintaxis.');
                }
              }}
              className="px-4 py-1.5 rounded text-xs font-semibold"
              style={{ background: 'var(--emp-accent)', color: 'white' }}>
              <CheckCircle2 size={12} className="inline mr-1" /> Aplicar
            </button>
          </div>
        </div>
      </div>,
      document.body
    )}

    </>
  );
}

export function LoteWorkspace(props: LoteWorkspaceProps) {
  return ReactDOM.createPortal(<Workspace {...props} />, document.body);
}
